import type { QueryClient } from '@tanstack/react-query';
import type { GetUserSettingsResponse } from 'generated/openapi';

import { getUserSettingsKey } from '@/features/notifications/hooks/useGetUserSettings/getUserSettings.options';
import { playSound, type SoundType } from '@/shared/utils/sound';

import {
  DEFAULT_SOUND_SETTINGS,
  type SoundNotificationType,
  type SoundSettings,
} from '../types';

type PlayableSoundType = 'notification' | 'message' | 'inChat' | 'send';

function getSoundSettingsFromCache(queryClient: QueryClient): SoundSettings {
  const data =
    queryClient.getQueryData<GetUserSettingsResponse>(getUserSettingsKey());

  if (!data) {
    return DEFAULT_SOUND_SETTINGS;
  }

  const settings = data.settings as SoundSettings & typeof data.settings;

  return {
    soundNotifications:
      settings.soundNotifications ?? DEFAULT_SOUND_SETTINGS.soundNotifications,
  };
}

/** Maps playable sound type to SoundNotificationType for settings check */
function getSoundNotificationType(
  type: PlayableSoundType,
): SoundNotificationType {
  switch (type) {
    case 'notification':
      return 'GENERAL';
    case 'message':
    case 'inChat':
      return 'CHAT_MESSAGE_RECEIVED';
    case 'send':
      return 'CHAT_MESSAGE_SENT';
  }
}

export function playSoundWithSettings(
  queryClient: QueryClient,
  type: PlayableSoundType,
): void {
  const settings = getSoundSettingsFromCache(queryClient);
  const notificationType = getSoundNotificationType(type);
  const shouldPlay = settings.soundNotifications.includes(notificationType);

  if (shouldPlay) {
    void playSound(type as SoundType);
  }
}
